/* eslint-disable camelcase */

import { NETWORK_ERROR_PATTERNS } from "common/utils/app-monitoring/log-upload-error";
import { hasBusinessOutcomeErrorBody } from "common/utils/app-monitoring/business-outcome-error";

const CLIENT_NOISE_PATTERNS = NETWORK_ERROR_PATTERNS.filter(
  pattern => pattern.toString() !== "/timeout/i"
);

const NOISE_ERROR_PATTERNS = [
  ...CLIENT_NOISE_PATTERNS,
  /Client Closed Request/i, // CloudFront client disconnect (status 499)
  /CanceledError/i // Axios request cancellation
];

// CloudFront WAF blocks — not actionable from app code
const CLOUDFRONT_BLOCK_PATTERN = /cloudfront/i;
// Bots hitting disallowed paths — expected noise
const ROBOTS_BLOCK_PATTERN = /robots disallow|robots\.txt/i;
// Generic responses from the Rails ErrorsController (config.exceptions_app = routes).
// Emitted when a request falls through to the global /404, /406, or /422
// handler — the application could not act on it, so the front-end cannot either.
// Exact-match, not substring, so app-specific bodies like
// {"error":"Order not found"} still report.
const RAILS_GENERIC_ERROR_BODIES = new Set([
  '{"error":"not_found"}',
  '{"error":"unprocessable_entity"}',
  '{"error":"not_acceptable"}'
]);
// Default nginx error page served when nginx routes a request but no upstream
// matches (e.g. stray URLs from old external links). Requires both the title
// and the nginx/ banner to avoid collision with app HTML that happens to
// contain "404 Not Found".
const NGINX_404_PATTERN = /<title>404 Not Found<\/title>[\s\S]*nginx\//i;
// Application redirect-flow responses — server is signaling a redirect to the
// browser (e.g. /cart/buy returning 422 with {"redirect_to":"/cart"} when the
// cart contents changed during checkout). The caller follows the redirect; the
// throw is control flow, not an error.
//
// Suppression is deliberately narrow — applied only to status 422 (caller
// site, see `isRelevantBrowserError` 4xx branch):
//  • Body must parse as a JSON object with `redirect_to` at the top level
//    (nested strings/objects mentioning redirect_to still report).
//  • `redirect_to` must be a same-origin relative path starting with `/` —
//    rejects `//attacker.com` (protocol-relative) and absolute URLs so an
//    endpoint reflecting attacker-controlled input cannot use this path to
//    suppress browser alerts. The check decodes URL-encoded paths so a value
//    like `/%2Fattacker.com` (which decodes to `//attacker.com`) is also
//    rejected.
const hasSameOriginRedirectTo = body => {
  if (!body || typeof body !== "string") return false;
  const trimmed = body.trim();
  if (!trimmed.startsWith("{")) return false;
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== "object") return false;
  const target = parsed.redirect_to;
  if (typeof target !== "string" || target.length === 0) return false;
  // Decode percent-encoding before the same-origin check so encoded variants
  // (e.g. `/%2Fattacker.com` → `//attacker.com`) cannot bypass the guard.
  // Malformed encodings (decodeURIComponent throws on `%`-without-2-hex) fall
  // through to "report" — matching the existing convention that anything we
  // can't safely classify as control flow is treated as a real error.
  let decoded;
  try {
    decoded = decodeURIComponent(target);
  } catch {
    return false;
  }
  return decoded.startsWith("/") && !decoded.startsWith("//");
};
// Application-level 404 control-flow responses — Spree controllers signal
// "resource missing / unauthorized" with {success: false, error|errors: ...}
// (e.g. /api/v2/order_surveys.json on /checkout/thankyou returning
// {"success":false,"errors":["Order not found or unauthorized"]} when the
// guest token has expired). The throw is server-driven control flow; the
// caller renders fallback UI or silently degrades, but request.js has
// already reported the raw response body to Honeybadger before the caller's
// catch runs.
//
// Suppression is deliberately narrow — applied only to status 404 (caller
// site, see `isRelevantBrowserError` 4xx branch):
//  • Body must parse as a JSON object with `success` strictly === false
//    at the top level (rejects coercion, string "false", missing key).
//  • Body must additionally carry either a non-empty `error` string OR a
//    top-level `errors` array — bare `{success:false}` is too sparse to
//    count as a deliberate control-flow signal.
//  • Nested matches (e.g. `errors:{redirect_to:["is invalid"]}`) do not
//    qualify — those are field-level validation results, not control flow.
const hasApplication404FailureBody = body => {
  if (!body || typeof body !== "string") return false;
  const trimmed = body.trim();
  if (!trimmed.startsWith("{")) return false;
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== "object") return false;
  if (parsed.success !== false) return false;
  const hasErrorString = typeof parsed.error === "string" && parsed.error.length > 0;
  const hasErrorsArray =
    Array.isArray(parsed.errors) &&
    parsed.errors.some(entry => typeof entry === "string" && entry.length > 0);
  return hasErrorString || hasErrorsArray;
};
// Known search and social crawlers. Bot traffic is not real-user RUM; any error
// a crawler hits is either expected noise (WAF blocks, robots.txt denials) or
// surfaced to us through server-side observability (Rails logs, AppMonitoring).
// Note: "whatsapp" is intentionally omitted — WhatsApp's in-app browser (real
// users tapping shared links) appends "WhatsApp/x.y.z" to a normal Chrome UA,
// and its link-preview crawler is already matched by "facebookexternalhit".
export const CRAWLER_UA_PATTERN =
  /bingbot|googlebot|applebot|duckduckbot|yandexbot|baiduspider|sogou|petalbot|ahrefsbot|semrushbot|mj12bot|dotbot|meta-externalads|facebookexternalhit|linkedinbot|twitterbot|slackbot|telegrambot|discordbot|pinterestbot/i;
// Infrastructure noise — not actionable from app code, tracked server-side
// 429 = rate-limited, 499 = client closed request
// 502/503/504 = gateway errors from Envoy/Istio/nginx during deploys and pod scaling
const IGNORED_STATUSES = new Set([429, 499, 502, 503, 504]);

export const isCrawlerUserAgent = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return typeof ua === "string" && CRAWLER_UA_PATTERN.test(ua);
};

const isNonEmptyString = value => typeof value === "string" && value.length > 0;

const safeJsonStringify = value => {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const toResponseBodyString = error => {
  const response = error?.response;

  if (typeof response === "string") return response;

  if (response?.data) {
    return typeof response.data === "string" ? response.data : safeJsonStringify(response.data);
  }

  if (
    response &&
    typeof response === "object" &&
    !("status" in response) &&
    !("data" in response)
  ) {
    return safeJsonStringify(response);
  }

  return "";
};

const toSearchableString = error =>
  [error?.message, error?.error, toResponseBodyString(error), error?.stack]
    .filter(isNonEmptyString)
    .join(" | ");

/**
 * Returns true if the error is worth sending to browser RUM via the notify shim.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export default function isRelevantBrowserError(error) {
  // Drop anything originating from a known crawler. Search and social bots
  // aren't real-user RUM; their errors show up as WAF blocks, robots.txt
  // denials, and pre-render quirks that server-side logs already capture.
  if (isCrawlerUserAgent()) return false;

  const status = error?.status ?? error?.response?.status;
  const haystack = toSearchableString(error);

  // Drop rate-limited and client-closed-request responses
  if (IGNORED_STATUSES.has(status)) return false;
  // Drop CloudFront WAF 403s (status-gated — CloudFront can appear in legitimate 5xx bodies)
  if (status === 403 && CLOUDFRONT_BLOCK_PATTERN.test(haystack)) return false;
  // Drop empty-body 403s — real app authorization failures include a JSON
  // payload; an empty body means the response came from edge infrastructure
  // (WAF / CDN bot challenge) and is not actionable from app code.
  if (status === 403 && toResponseBodyString(error) === "") return false;

  // Report application server errors (500, 501) — 502/503/504 are filtered above by IGNORED_STATUSES
  if (typeof status === "number" && status >= 500) return true;

  // Drop generic ErrorsController JSON bodies and nginx 404 HTML on 4xx. Placed
  // after the 5xx guard so a real 500 carrying the same body still reports.
  if (typeof status === "number" && status >= 400 && status < 500) {
    const body = toResponseBodyString(error);
    if (RAILS_GENERIC_ERROR_BODIES.has(body)) return false;
    if (NGINX_404_PATTERN.test(body)) return false;
    if (status === 422 && hasSameOriginRedirectTo(body)) return false;
    if (status === 422 && hasBusinessOutcomeErrorBody(body)) return false;
    if (status === 404 && hasApplication404FailureBody(body)) return false;
  }

  // Drop robots.txt / robots-disallow noise from bots — placed after the 5xx guard
  // so a 500 whose body mentions robots.txt is still reported as an application error.
  // Gated on "no HTTP status or 403" so a real 4xx application error whose body
  // happens to mention robots.txt is still reported; HB-1743 needs 403+robots filtered.
  if ((typeof status !== "number" || status === 403) && ROBOTS_BLOCK_PATTERN.test(haystack))
    return false;

  // Drop network/cancellation noise that is not actionable from app code.
  // Only apply to errors without an HTTP status — a real 4xx whose response body
  // happens to contain "canceled" or "offline" must still be reported.
  if (typeof status !== "number" && NOISE_ERROR_PATTERNS.some(pattern => pattern.test(haystack)))
    return false;

  return true;
}

/* eslint-enable camelcase */
